import React from 'react';

function Support() {
  return (
    <main>
      <div className="page-container">
        <h1>Support</h1>
        <p style={{ fontSize: '1.1rem', color: '#555' }}>
          Welcome to SwiftBreak Support. Find answers to common questions and learn how to get the most out of the game.
        </p>

        <h2>Getting Started</h2>
        
        <h3>How to Play</h3>
        <p>
          SwiftBreak is easy to learn but challenging to master. Touch and drag away from the cue ball 
          to aim your shot. The further you pull, the more power you'll apply. Release to shoot!
        </p>
        <ul>
          <li><strong>Aim:</strong> Touch and drag away from the cue ball</li>
          <li><strong>Power:</strong> Pull distance determines shot strength</li>
          <li><strong>Shoot:</strong> Release your finger to execute the shot</li>
          <li><strong>Continue:</strong> Keep shooting as long as you're potting balls</li>
        </ul>

        <h3>Game Modes</h3>
        <ul>
          <li>
            <strong>Classic Mode:</strong> Traditional pool rules with 7 balls. Pot any ball to 
            continue your turn. Perfect for competitive matches.
          </li>
          <li>
            <strong>Quick Play Mode:</strong> Faster gameplay with only 4 balls. Ideal for quick 
            sessions when you're short on time.
          </li>
          <li>
            <strong>Challenge Mode:</strong> Time-limited mode with a 90-second countdown. Clear 
            the table before time runs out and maximize your score!
          </li>
        </ul>

        <h2>Features</h2>

        <h3>Achievements</h3>
        <p>
          Unlock 13 unique achievements by reaching various milestones. Achievements range from 
          Common to Legendary rarity. Track your progress in the Achievements menu.
        </p>

        <h3>Statistics</h3>
        <p>
          View detailed statistics including:
        </p>
        <ul>
          <li>Total matches played and win rate</li>
          <li>Current and best win streaks</li>
          <li>Shooting performance (shots, balls potted, averages)</li>
          <li>Mode-specific statistics</li>
          <li>Challenge mode high scores</li>
          <li>Quickest win times</li>
        </ul>

        <h3>Customization</h3>
        <p>
          Personalize your experience in the Settings menu:
        </p>
        <ul>
          <li><strong>Table Themes:</strong> Choose from 4 beautiful themes (Classic Green, Modern Blue, Midnight, Ocean)</li>
          <li><strong>Aim Guide:</strong> Toggle the aiming assistance line</li>
          <li><strong>Power Indicator:</strong> Show or hide the power ring around the cue ball</li>
          <li><strong>Sound Effects:</strong> Enable or disable game sounds</li>
          <li><strong>Haptic Feedback:</strong> Toggle vibration feedback for shots and events</li>
        </ul>

        <h2>Troubleshooting</h2>

        <h3>Game Performance Issues</h3>
        <p>
          If you experience lag or performance issues:
        </p>
        <ul>
          <li>Close other apps running in the background</li>
          <li>Restart the SwiftBreak app</li>
          <li>Ensure your device is running the latest iOS version</li>
          <li>Check that you have sufficient storage space available</li>
        </ul>

        <h3>Touch Controls Not Responding</h3>
        <p>
          If touch controls feel unresponsive:
        </p>
        <ul>
          <li>Make sure your screen is clean and dry</li>
          <li>Remove any screen protectors that may interfere with touch sensitivity</li>
          <li>Try restarting your device</li>
          <li>Check if the issue occurs with other apps (may indicate a hardware problem)</li>
        </ul>

        <h3>Progress Not Saving</h3>
        <p>
          Your statistics and achievements are automatically saved to your device. If progress 
          isn't being saved:
        </p>
        <ul>
          <li>Ensure you're not force-closing the app immediately after playing</li>
          <li>Check that SwiftBreak has sufficient storage permissions</li>
          <li>Try reinstalling the app (note: this will reset your progress)</li>
        </ul>

        <h3>Sound or Haptics Not Working</h3>
        <p>
          If you're not hearing sounds or feeling haptic feedback:
        </p>
        <ul>
          <li>Check that sound effects are enabled in Settings</li>
          <li>Verify that haptic feedback is enabled in Settings</li>
          <li>Ensure your device is not in silent mode</li>
          <li>Check your device's volume settings</li>
          <li>Verify that haptics are enabled in your device's system settings</li>
        </ul>

        <h2>System Requirements</h2>
        <ul>
          <li><strong>Platform:</strong> iOS 16.2 or later</li>
          <li><strong>Devices:</strong> iPhone and iPad</li>
          <li><strong>Storage:</strong> Approximately 50 MB</li>
          <li><strong>Languages:</strong> English, Simplified Chinese, Japanese, German, French, Italian, Spanish</li>
        </ul>

        <h2>Tips & Tricks</h2>

        <h3>Master Your Shots</h3>
        <ul>
          <li>Use the aim guide to line up difficult shots</li>
          <li>Watch the power indicator to control shot strength</li>
          <li>Practice with different power levels to learn ball control</li>
          <li>Plan ahead - think about your next shot after potting a ball</li>
        </ul>

        <h3>Winning Strategies</h3>
        <ul>
          <li>Try to pot multiple balls in one turn to build momentum</li>
          <li>Position the cue ball strategically for your next shot</li>
          <li>In Challenge mode, prioritize speed over perfection</li>
          <li>Learn the physics - understand how balls bounce and interact</li>
        </ul>

        <h3>Achievement Hunting</h3>
        <ul>
          <li>Focus on "Clean Sweep" by clearing all balls in one turn</li>
          <li>Build win streaks for "On Fire" and "Unstoppable" achievements</li>
          <li>Try different game modes to unlock mode-specific achievements</li>
          <li>The "Sniper" achievement requires potting 3 balls in a single shot - aim for clusters!</li>
        </ul>

        <h2>Contact Support</h2>
        <div className="contact-info">
          <h3>Need More Help?</h3>
          <p>
            If you couldn't find the answer to your question, please contact us:
          </p>
          <p>
            <strong>Email:</strong> <a href="mailto:wsmslgh@gmail.com">wsmslgh@gmail.com</a>
          </p>
          <p>
            When contacting support, please include:
          </p>
          <ul style={{ marginLeft: '1.5rem' }}>
            <li>Your device model and iOS version</li>
            <li>A detailed description of the issue</li>
            <li>Steps to reproduce the problem (if applicable)</li>
            <li>Screenshots or screen recordings (if relevant)</li>
          </ul>
          <p>
            We typically respond within 24-48 hours during business days.
          </p>
        </div>

        <h2>Frequently Asked Questions</h2>

        <h3>Is SwiftBreak free to play?</h3>
        <p>
          Yes! SwiftBreak is completely free to download and play with no ads or in-app purchases 
          required to access any features.
        </p>

        <h3>Can I play online with friends?</h3>
        <p>
          Currently, SwiftBreak supports pass-and-play mode where you can play with a friend on 
          the same device. Online multiplayer is being considered for future updates.
        </p>

        <h3>Will my progress sync across devices?</h3>
        <p>
          Currently, progress is stored locally on your device. Cloud sync functionality may be 
          added in a future update.
        </p>

        <h3>How do I reset the tutorial?</h3>
        <p>
          You can reset the tutorial in Settings. Scroll down to "About" section and tap 
          "Reset Tutorial". The tutorial will show again on your next app launch.
        </p>

        <h3>Can I disable the aim guide?</h3>
        <p>
          Yes! Go to Settings &gt; Gameplay and toggle "Show Aim Guide" off. This adds an 
          extra challenge for experienced players.
        </p>

        <h3>How do I change the table theme?</h3>
        <p>
          Open Settings &gt; Appearance &gt; Table Theme and select from Classic Green, 
          Modern Blue, Midnight, or Ocean themes.
        </p>

        <h3>Does SwiftBreak drain battery quickly?</h3>
        <p>
          SwiftBreak is optimized for performance and battery efficiency. It runs at 60 FPS 
          with efficient rendering to minimize battery usage.
        </p>

        <h2>Feedback & Suggestions</h2>
        <p>
          We'd love to hear your feedback and suggestions for improving SwiftBreak! 
          Please email us at <a href="mailto:wsmslgh@gmail.com">wsmslgh@gmail.com</a> 
          or leave a review on the App Store.
        </p>
        <p>
          Your input helps us make SwiftBreak better for everyone!
        </p>
      </div>
    </main>
  );
}

export default Support;
